import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  FormFeedback,
  ButtonGroup,
} from "reactstrap";
import DatePicker from "react-datepicker";
import { useNavigate, useParams } from "react-router-dom";
import { get, put, post, uri } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import { useFormik } from "formik";
import * as Yup from "yup";
import secureLocalStorage from "react-secure-storage";
import Footer from "components/VerticalLayout/Footer";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "helpers/toastMessages";
import DragAndDropComponent from "components/FileUploadDrag/DragAndDrop";

const AddTimeSheet = () => {
  const [selectedDate, setSelectedDate] = useState(new Date()); // Default to current date for add mode
  const [weekEntries, setWeekEntries] = useState({});
  const [timeSheetData, setTimeSheetData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isManager, setIsManager] = useState(false);
  const [timesheetType, setTimesheetType] = useState("my");
  const navigate = useNavigate();
  const { timeSheetId } = useParams();
  const employeeId = secureLocalStorage.getItem("employeeId");
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [managerEmployees, setManagerEmployees] = useState([]);
  const [message, setMessage] = useState();

  useEffect(() => {
    if (timeSheetData && employeeId) {
      const timesheetEmployeeId = timeSheetData.employeeId.toString();
      if (timesheetEmployeeId !== employeeId) {
        setTimesheetType("employee");
        validation.setFieldValue("selectedEmployee", timesheetEmployeeId);
      } else {
        setTimesheetType("my");
      }
      // Set selectedDate based on periodStart in edit mode
      if (timeSheetData.periodStart) {
        setSelectedDate(new Date(timeSheetData.periodStart));
      }
    }
  }, [timeSheetData, employeeId]);

  useEffect(() => {
    Promise.all([
      fetchProjectsData(employeeId),
      fetchEmployeesData(),
      fetchTimesheets(),
      fetchManagerEmployeesData(employeeId),
      timeSheetId ? fetchTimesheetData(timeSheetId) : Promise.resolve(),
    ]);
  }, [timeSheetId]);

  const fetchTimesheets = async () => {
    try {
      const employeeId = JSON.parse(secureLocalStorage.getItem("employeeId"));
      const response = await post(api.GET_ALL_TIMESHEETS_BY_SEARCH, {
        search: "",
        sortBy: "timesheetCode",
        direction: "ASC",
        timesheetDTO: {
          subscriptionId: 100,
          employeeId: parseInt(employeeId) ? parseInt(employeeId) : null,
        },
      });
      if (response?.paginationResponse?.data?.length > 0) {
        setIsManager(response.paginationResponse.data[0].manager);
      }
    } catch (error) {
      console.error("Error fetching timesheets:", error);
    }
  };

  const fetchEmployeesData = async () => {
    try {
      const employeesData = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
      const updatedEmployeeData = employeesData?.map((item) => ({
        id: item.employeeId,
        value: item.employeeId,
        name: `${item.firstName} ${item.lastName} - (${item.employeeCode})`,
      }));
      setEmployees(updatedEmployeeData);
    } catch (error) {
      console.error("Error fetching employees data", error);
    }
  };

  const fetchProjectsData = async (empId) => {
    try {
      let projectsData;
      if (empId !== null && empId !== undefined && empId !== "null") {
        console.log("Fetching projects for employee ID:", empId);
        projectsData = await get(api.GET_ALL_PROJECTS_ID_NAME(empId));
      } else {
        console.log("Employee ID is missing, using fallback API");
        projectsData = await get(api.GET_ALL_PROJECTS_ID_NAMES);
      }

      const updatedProjectData =
        projectsData?.paginationResponse?.data.map((item) => ({
          id: item.projectId,
          value: item.projectId,
          name: `${item.projectName} - (${item.projectCode})`,
        })) || [];
      setProjects(updatedProjectData);
    } catch (error) {
      console.error(
        `Error fetching projects for employee${empId ? ` ${empId}` : ""}:`,
        error
      );
      setProjects([]);
    }
  };

  const fetchAssignmentsData = async (empId, projectId) => {
    try {
      let AssignData;
      if (empId !== null && empId !== undefined && empId !== "null") {
        AssignData = await get(api.GET_ALL_ASSIGNMENTS_ID_NAMES(empId, projectId));
      } else {
        AssignData = await get(api.GET_ALL_ASSIGNMENTS_ID_NAME);
      }
      const updatedAssignData =
        AssignData?.data?.map((item) => ({
          id: item.assignmentId,
          value: item.assignmentId,
          name: `${item.assignmentTitle} - (${item.assignmentCode})`,
        })) || [];
      setAssignments(updatedAssignData);
    } catch (error) {
      console.error(
        `Error fetching assignments data${
          empId && projectId ? ` for employee ${empId} and project ${projectId}` : ""
        }:`,
        error
      );
      setAssignments([]);
    }
  };

  const fetchTimesheetData = async (timeSheetId) => {
    try {
      const response = await get(api.GET_TIMESHEET_BY_ID(timeSheetId));
      setTimeSheetData(response);

      if (response?.projectId) {
        fetchAssignmentsData(
          timesheetType === "my" ? employeeId : response.employeeId,
          response.projectId
        );
      }

      // Populate weekEntries with existing timesheet data or default to 8.0 for weekdays, 0.0 for weekends
      if (response?.dailyHours) {
        const updatedWeekEntries = {};
        const days = generateCalendarDays(new Date(response.periodStart));
        days.forEach((day) => {
          const dateKey = day.date.toISOString().split("T")[0];
          const dayName = day.dayName.toLowerCase();
          updatedWeekEntries[dayName] = {
            date: dateKey,
            hours: response.dailyHours[dateKey]?.hours?.toString() || (day.isWeekend ? "0.0" : "8.0"),
            description: response.dailyHours[dateKey]?.description || "",
          };
        });
        setWeekEntries(updatedWeekEntries);
      }

      const clientDocuments = response?.clientDocuments || [];
      if (parseInt(clientDocuments)) {
        const fileResponse = await get(
          `${uri}/fileservice/api/files/view/${clientDocuments}`
        );
        const initialUploadedFiles = {
          id: fileResponse.id,
          fileName: fileResponse.fileName,
          name: fileResponse.fileName,
        };
        setUploadedFiles(initialUploadedFiles);
      }
    } catch (error) {
      console.error("Error fetching timesheet data:", error);
    }
  };

  const fetchManagerEmployeesData = async (employeeId) => {
    if (employeeId !== null && employeeId !== undefined && employeeId !== "null") {
      try {
        const response = await get(api.GET_MANAGER_EMPLOYEES_LIST(employeeId));
        const employeeData = response?.data.map((employee) => ({
          id: employee.employeeId,
          value: employee.employeeId,
          name: `${employee.firstName} ${employee.lastName}`,
        }));
        setManagerEmployees(employeeData);
      } catch (error) {
        showErrorToast("Manager employees fetch failed");
      }
    }
  };

  const generateCalendarDays = (startDate) => {
    const days = [];
    const date = new Date(startDate);
    date.setDate(date.getDate() - date.getDay());
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(date);
      currentDate.setDate(date.getDate() + i);
      days.push({
        date: currentDate,
        dayName: currentDate.toLocaleDateString("en-US", { weekday: "long" }),
        formattedDate: currentDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        isWeekend: currentDate.getDay() === 0 || currentDate.getDay() === 6,
      });
    }
    return days;
  };

  // Initialize weekEntries with default 8.0 hours for weekdays, 0.0 for weekends
  useEffect(() => {
    const days = generateCalendarDays(selectedDate);
    const updatedWeekEntries = {};
    days.forEach((day) => {
      const dayName = day.dayName.toLowerCase();
      const dateKey = day.date.toISOString().split("T")[0];
      updatedWeekEntries[dayName] = {
        date: dateKey,
        hours: timeSheetData?.dailyHours?.[dateKey]?.hours?.toString() || (day.isWeekend ? "0" : "8"),
        description: timeSheetData?.dailyHours?.[dateKey]?.description || "",
      };
    });
    setWeekEntries(updatedWeekEntries);
  }, [selectedDate, timeSheetData]);

  const calendarDays = generateCalendarDays(selectedDate);

  const handleEntryChange = (day, field, value) => {
    if (field === "hours") {
      const regex = /^\d*\.?\d*$/;
      if (!regex.test(value)) return;
      if (value.includes(".") && value.split(".")[1]?.length > 2) return;
      if (parseFloat(value) > 24) return;
    }

    setWeekEntries((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const handleSave = (status) => {
    validation.setFieldValue("status", status);
    validation.handleSubmit();
  };

  const getWeekTotal = () => {
    return Object.values(weekEntries)
      .reduce((total, day) => total + (parseFloat(day.hours) || 0), 0)
      .toFixed(2);
  };

  const transformDailyHours = () => {
    const dailyHours = {};
    Object.entries(weekEntries).forEach(([day, entry]) => {
      const hours = parseFloat(entry.hours);
      const description = entry.description;
      if (hours >= 0) {
        dailyHours[entry.date] = {
          hours: hours,
          description: description,
        };
      }
    });
    return dailyHours;
  };

  const handleFileUpload = (response) => {
    setUploadedFiles({ ...response, name: response.fileName });
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      selectedProject: timeSheetData?.projectId?.toString() || "",
      selectedAssignment: timeSheetData?.assignmentId?.toString() || "",
      selectedEmployee:
        timeSheetData?.employeeId?.toString() ||
        (timesheetType === "my" ? employeeId : ""),
      status: timeSheetData?.status.toString() || "",
      clientDocuments: timeSheetData?.clientDocuments || "",
    },
    validationSchema: Yup.object({
      selectedProject: Yup.string().required("Project is required"),
      selectedAssignment: Yup.string().required("Assignment is required"),
      selectedEmployee: Yup.string().test(
        "employee-required",
        "Employee is required",
        function (value) {
          if (isManager && timesheetType === "employee") {
            return !!value;
          }
          return true;
        }
      ),
    }),
    onSubmit: async (values) => {
      const date = new Date(selectedDate);
      date.setDate(date.getDate() - date.getDay());

      const payload = {
        employeeId: parseInt(
          timesheetType === "my" ? employeeId : values.selectedEmployee
        ),
        subscriptionId: 100,
        periodStart: date.toISOString().split("T")[0],
        dailyHours: transformDailyHours(),
        projectId: parseInt(values.selectedProject),
        assignmentId: parseInt(values.selectedAssignment),
        status: values.status,
        clientDocuments: uploadedFiles?.id,
      };

      try {
        let response;
        if (timeSheetId) {
          response = await put(api.PUT_TIMESHEET_UPDATE(timeSheetId), payload);
          setMessage(response?.message);
          showSuccessToast("Timesheet updated successfully");
        } else {
          response = await post(api.POST_TIMESHEET_SUBMIT, payload);
          showSuccessToast("Timesheet submitted successfully");
        }

        if (response) {
          navigate("/timesheets");
        } else {
          showErrorToast("Failed to submit/update timesheet");
        }
      } catch (error) {
        console.error("Submission error:", error.message, error.stack);
        showErrorToast("Something went wrong");
      }
    },
  });

  const handleProjectChange = (e) => {
    const selectedProjectId = e.target.value;
    validation.handleChange(e);
    if (selectedProjectId) {
      fetchAssignmentsData(
        timesheetType === "my" ? employeeId : validation.values.selectedEmployee,
        selectedProjectId
      );
    } else {
      setAssignments([]);
      validation.setFieldValue("selectedAssignment", "");
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="py-0 px-0">
        <PageSubHeader
          heading={timeSheetId ? "Update Timesheet" : "Add Timesheet"}
          routePath="Home"
          pageName="Timesheet"
        />

        <Row className="p-4">
          {isManager && (
            <Row className="mb-4">
              <Col>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <ButtonGroup>
                    <Button
                      color={timesheetType === "my" ? "primary" : "secondary"}
                      outline={timesheetType !== "my"}
                      onClick={() => {
                        setTimesheetType("my");
                        setTimeSheetData(null);
                        validation.setFieldValue("selectedEmployee", employeeId);
                        setAssignments([]);
                        validation.setFieldValue("selectedAssignment", "");
                        validation.setFieldValue("selectedProject", "");
                        fetchProjectsData(employeeId);
                        setSelectedDate(new Date()); // Reset to current date in "My Timesheet" mode
                      }}
                    >
                      My Timesheet
                    </Button>
                    <Button
                      color={timesheetType === "employee" ? "primary" : "secondary"}
                      outline={timesheetType !== "employee"}
                      onClick={() => {
                        setTimesheetType("employee");
                        setTimeSheetData(null);
                        validation.setFieldValue("selectedEmployee", "");
                        setAssignments([]);
                        validation.setFieldValue("selectedAssignment", "");
                        validation.setFieldValue("selectedProject", "");
                        setProjects([]);
                        setSelectedDate(new Date()); // Reset to current date in "Employee Timesheet" mode
                      }}
                    >
                      Employee Timesheet
                    </Button>
                  </ButtonGroup>
                </div>
              </Col>
            </Row>
          )}

          <Col>
            <Card>
              <CardBody>
                <Form onSubmit={validation.handleSubmit}>
                  <Row className="mb-3 g-3">
                    {isManager && timesheetType === "employee" && (
                      <Col md="3">
                        <FormGroup>
                          <Label className="fw-bold">Select Employee</Label>
                          <Input
                            type="select"
                            name="selectedEmployee"
                            value={validation.values.selectedEmployee}
                            onChange={(e) => {
                              validation.handleChange(e);
                              setAssignments([]);
                              validation.setFieldValue("selectedProject", "");
                              validation.setFieldValue("selectedAssignment", "");
                              if (e.target.value) {
                                fetchProjectsData(e.target.value);
                              } else {
                                setProjects([]);
                              }
                            }}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.selectedEmployee &&
                              !!validation.errors.selectedEmployee
                            }
                            disabled={timeSheetId && timeSheetData}
                          >
                            <option value="">Select Employee</option>
                            {managerEmployees?.map((employee) => (
                              <option key={employee.id} value={employee.id}>
                                {employee.name}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.selectedEmployee &&
                            validation.errors.selectedEmployee && (
                              <FormFeedback>
                                {validation.errors.selectedEmployee}
                              </FormFeedback>
                            )}
                        </FormGroup>
                      </Col>
                    )}
                    <Col md="3">
                      <FormGroup>
                        <Label className="fw-bold">Select Week</Label>
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => setSelectedDate(date)}
                          dateFormat="MM/dd/yyyy"
                          className="form-control"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label className="fw-bold">Select Project</Label>
                        <Input
                          type="select"
                          name="selectedProject"
                          value={validation.values.selectedProject}
                          onChange={handleProjectChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.selectedProject &&
                            !!validation.errors.selectedProject
                          }
                        >
                          <option value="">Select Project</option>
                          {projects?.map((project) => (
                            <option key={project.id} value={project.value}>
                              {project.name}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.selectedProject &&
                          validation.errors.selectedProject && (
                            <FormFeedback>
                              {validation.errors.selectedProject}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label className="fw-bold">Select Assignment</Label>
                        <Input
                          type="select"
                          name="selectedAssignment"
                          value={validation.values.selectedAssignment}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.selectedAssignment &&
                            !!validation.errors.selectedAssignment
                          }
                        >
                          <option value="">Select Assignment</option>
                          {assignments?.map((assignment) => (
                            <option key={assignment.id} value={assignment.value}>
                              {assignment.name}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.selectedAssignment &&
                          validation.errors.selectedAssignment && (
                            <FormFeedback>
                              {validation.errors.selectedAssignment}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {timeSheetId &&
                    timeSheetData?.status === "REJECTED" &&
                    timeSheetData?.rejectionReason && (
                      <Row className="mb-4">
                        <Col>
                          <Card className="border-danger">
                            <CardBody>
                              <p className="mb-0 text-danger fw-bold">
                                Rejection Reason:{" "}
                                <span className="fw-normal text-dark px-2">
                                  {timeSheetData.rejectionReason}
                                </span>
                              </p>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    )}

                  <div className="table-responsive">
                    <Table bordered hover className="border shadow-sm">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: "20%" }}>Day</th>
                          <th style={{ width: "20%" }}>Hours</th>
                          <th style={{ width: "60%" }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calendarDays?.map((day) => (
                          <tr key={day.dayName}>
                            <td className="align-middle">
                              <div className="fw-bold">{day.dayName}</div>
                              <div className="text-muted small">
                                {day.formattedDate}
                              </div>
                            </td>
                            <td>
                              <Input
                                type="text"
                                value={weekEntries[day.dayName.toLowerCase()]?.hours}
                                onChange={(e) =>
                                  handleEntryChange(
                                    day.dayName.toLowerCase(),
                                    "hours",
                                    e.target.value
                                  )
                                }
                                placeholder="0.0"
                                className={`text-center ${day.isWeekend ? "bg-light" : ""}`}
                              />
                            </td>
                            <td>
                              <Input
                                type="textarea"
                                rows="1"
                                value={weekEntries[day.dayName.toLowerCase()]?.description}
                                onChange={(e) =>
                                  handleEntryChange(
                                    day.dayName.toLowerCase(),
                                    "description",
                                    e.target.value
                                  )
                                }
                                placeholder="Add description"
                                className={day.isWeekend ? "bg-light" : ""}
                              />
                            </td>
                          </tr>
                        ))}
                        <tr className="bg-light fw-bold">
                          <td colSpan="2" className="text-end">
                            Total Hours:
                          </td>
                          <td className="text-center">{getWeekTotal()}</td>
                        </tr>
                      </tbody>
                    </Table>

                    <div>
                      <Col md={3}>
                        <Label className="fw-bold">Client Document</Label>
                        <DragAndDropComponent
                          initialFile={uploadedFiles}
                          handleFileUpload={handleFileUpload}
                        />
                      </Col>
                    </div>
                  </div>
                  <Footer
                    saveText={timeSheetId ? "Update and Submit" : "Submit"}
                    save={() => handleSave("SUBMITTED")}
                    buttons={[
                      {
                        text: "Draft",
                        color: "primary",
                        onClick: () => handleSave("DRAFT"),
                      },
                    ]}
                  />
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddTimeSheet;